import { useEffect, useState } from 'react';

const reduceMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const useScrollClass = (threshold = 80) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);
  return scrolled;
};

export const useReveal = () => {
  useEffect(() => {
    const reveals = document.querySelectorAll<HTMLElement>('.reveal');
    if (!reveals.length) return;
    if (reduceMotion()) {
      reveals.forEach(el => el.classList.add('visible'));
      return;
    }
    const ro = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            e.target.classList.add('visible');
            ro.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
    );
    reveals.forEach(el => ro.observe(el));
    return () => ro.disconnect();
  }, []);
};

export const useStatCountUp = (
  ref: React.RefObject<HTMLElement>,
  target: number,
  decimals: number,
  suffix: string
) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const finalText = target.toFixed(decimals) + suffix;
    if (reduceMotion()) {
      el.textContent = finalText;
      return;
    }
    let raf = 0;
    let started = false;
    const animate = () => {
      const duration = 1200;
      const start = performance.now();
      const ease = (t: number) => 1 - Math.pow(1 - t, 3);
      const step = (now: number) => {
        const t = Math.min((now - start) / duration, 1);
        el.textContent = (target * ease(t)).toFixed(decimals) + suffix;
        if (t < 1) raf = requestAnimationFrame(step);
        else el.textContent = finalText;
      };
      raf = requestAnimationFrame(step);
    };
    const so = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          if (e.isIntersecting && !started) {
            started = true;
            animate();
            so.unobserve(el);
          }
        });
      },
      { threshold: 0.4 }
    );
    so.observe(el);
    return () => {
      so.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [ref, target, decimals, suffix]);
};

export const useHeroGlow = (heroRef: React.RefObject<HTMLElement>) => {
  useEffect(() => {
    const hero = heroRef.current;
    if (!hero || reduceMotion()) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const glow = hero.querySelector<HTMLElement>('.hero-glow');
    if (!glow) return;

    let raf = 0;
    let tx = 50;
    let ty = 40;
    let cx = 50;
    let cy = 40;
    const apply = () => {
      cx += (tx - cx) * 0.12;
      cy += (ty - cy) * 0.12;
      glow.style.setProperty('--mx', cx + '%');
      glow.style.setProperty('--my', cy + '%');
      raf = Math.abs(tx - cx) > 0.1 || Math.abs(ty - cy) > 0.1 ? requestAnimationFrame(apply) : 0;
    };

    const onEnter = () => hero.classList.add('glow-on');
    const onLeave = () => hero.classList.remove('glow-on');
    const onMove = (e: MouseEvent) => {
      const r = hero.getBoundingClientRect();
      tx = ((e.clientX - r.left) / r.width) * 100;
      ty = ((e.clientY - r.top) / r.height) * 100;
      if (!raf) raf = requestAnimationFrame(apply);
    };
    hero.addEventListener('mouseenter', onEnter);
    hero.addEventListener('mouseleave', onLeave);
    hero.addEventListener('mousemove', onMove);
    return () => {
      hero.removeEventListener('mouseenter', onEnter);
      hero.removeEventListener('mouseleave', onLeave);
      hero.removeEventListener('mousemove', onMove);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [heroRef]);
};

export const usePhoneCarousel = (
  containerRef: React.RefObject<HTMLElement>,
  count: number,
  intervalMs = 3500
) => {
  const [active, setActive] = useState(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || count <= 1 || reduceMotion()) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    let hovered = false;
    let inView = true;
    const tick = () => setActive(prev => (prev + 1) % count);
    const start = () => {
      if (!timer && !hovered && inView) timer = setInterval(tick, intervalMs);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onEnter = () => {
      hovered = true;
      stop();
    };
    const onLeave = () => {
      hovered = false;
      start();
    };
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);
    const vio = new IntersectionObserver(
      entries => {
        entries.forEach(e => {
          inView = e.isIntersecting;
          inView ? start() : stop();
        });
      },
      { threshold: 0.2 }
    );
    vio.observe(el);
    start();
    return () => {
      stop();
      vio.disconnect();
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, [containerRef, count, intervalMs]);
  return active;
};
